"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { useAuth } from "@/lib/auth-context";
import { apiGetQuiet } from "@/lib/api";

interface UserRow {
  username: string;
  status?: string;
}

export default function UserMenu() {
  const { user, isSudo, impersonator, impersonate, stopImpersonate, logout } = useAuth();
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState<UserRow[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Capture + deferred dismiss so clicks inside modals (stopPropagation) still close us.
  useEffect(() => {
    if (!open) return;
    const dismiss = () => { setTimeout(() => setOpen(false), 0); };
    const id = requestAnimationFrame(() => {
      document.addEventListener("click", dismiss, true);
    });
    return () => { cancelAnimationFrame(id); document.removeEventListener("click", dismiss, true); };
  }, [open]);

  // Load the user list only for sudo admins when the menu opens.
  useEffect(() => {
    if (!open || !isSudo) return;
    let cancelled = false;
    setError(null);
    apiGetQuiet<{ users: UserRow[] }>("/admin/users")
      .then((r) => {
        if (cancelled) return;
        const list = (r.users ?? [])
          .filter((u) => u.status !== "disabled")
          .sort((a, b) => a.username.localeCompare(b.username));
        setUsers(list);
      })
      .catch(() => {
        if (!cancelled) setUsers([]);
      });
    return () => { cancelled = true; };
  }, [open, isSudo]);

  if (!user) return null;

  const handleLogout = async () => {
    setOpen(false);
    await logout();
    router.push("/");
  };

  const handleImpersonate = async (username: string) => {
    if (username === user && !impersonator) return;
    setBusy(username);
    setError(null);
    try {
      if (impersonator && username === impersonator) {
        await stopImpersonate();
      } else {
        await impersonate(username);
      }
      setOpen(false);
      // Full reload so every page refetches data under the new principal.
      window.location.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to switch user");
      setBusy(null);
    }
  };

  const handleStop = async () => {
    setBusy("__stop__");
    setError(null);
    try {
      await stopImpersonate();
      setOpen(false);
      window.location.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to stop impersonation");
      setBusy(null);
    }
  };

  return (
    <div className="relative">
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
        className="text-xs text-muted hover:text-foreground transition-colors select-none text-right"
      >
        {impersonator ? (
          <>
            <span className="text-red font-medium">Viewing as: {user}</span>
            <span className="block text-[10px] text-muted">admin: {impersonator}</span>
          </>
        ) : (
          <>
            Logged in as: <span className="text-foreground font-medium">{user}</span>
          </>
        )}
      </button>
      {open && (
        <div
          className="dropdown-panel absolute right-0 top-full mt-1 min-w-[180px] max-h-[70vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {impersonator && (
            <button
              onClick={handleStop}
              disabled={busy != null}
              className="dropdown-item text-red font-medium"
            >
              {busy === "__stop__" ? "Restoring…" : `Back to ${impersonator}`}
            </button>
          )}
          {isSudo && users.length > 0 && (
            <>
              <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wide text-muted border-t border-border first:border-t-0">
                View as user
              </div>
              {users.map((u) => {
                const active = u.username === user;
                return (
                  <button
                    key={u.username}
                    onClick={() => handleImpersonate(u.username)}
                    disabled={busy != null || (active && !impersonator)}
                    className={`dropdown-item font-mono ${active ? "text-cyan" : ""}`}
                    title={active ? "Currently viewing" : `View app as ${u.username}`}
                  >
                    {busy === u.username ? "…" : u.username}
                    {active && <span className="ml-2 text-[10px] text-muted">current</span>}
                  </button>
                );
              })}
            </>
          )}
          {error && (
            <div className="px-3 py-1.5 text-[11px] text-red border-t border-border">{error}</div>
          )}
          <button
            onClick={handleLogout}
            className="dropdown-item border-t border-border"
          >
            Logout
          </button>
        </div>
      )}
    </div>
  );
}
